import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import PrimaryNavigation from '../../components/ui/PrimaryNavigation';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import { useAuth } from '../../contexts/AuthContext';
import { useTranslation } from 'react-i18next';

const UserGuide = () => {
  const navigate = useNavigate();
  const { profile } = useAuth();
  const { t } = useTranslation();
  const [activeVideo, setActiveVideo] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState('all');

  const videoTutorials = [
    {
      id: 1,
      title: t('tutorials.tutorial1.title'),
      category: "basics",
      duration: "5:30",
      description: t('tutorials.tutorial1.description'),
      thumbnail: "🎬",
      steps: [
        t('tutorials.tutorial1.step1'),
        t('tutorials.tutorial1.step2'),
        t('tutorials.tutorial1.step3'),
        t('tutorials.tutorial1.step4'),
        t('tutorials.tutorial1.step5')
      ]
    },
    {
      id: 2,
      title: t('tutorials.tutorial2.title'),
      category: "recommendations",
      duration: "8:45",
      description: t('tutorials.tutorial2.description'),
      thumbnail: "🤖",
      steps: [
        t('tutorials.tutorial2.step1'),
        t('tutorials.tutorial2.step2'),
        t('tutorials.tutorial2.step3'),
        t('tutorials.tutorial2.step4'),
        t('tutorials.tutorial2.step5'),
        t('tutorials.tutorial2.step6'),
        t('tutorials.tutorial2.step7')
      ]
    },
    {
      id: 3,
      title: t('tutorials.tutorial3.title'),
      category: "billing",
      duration: "4:15",
      description: t('tutorials.tutorial3.description'),
      thumbnail: "💳",
      steps: [
        t('tutorials.tutorial3.step1'),
        t('tutorials.tutorial3.step2'),
        t('tutorials.tutorial3.step3'),
        t('tutorials.tutorial3.step4'),
        t('tutorials.tutorial3.step5'),
        t('tutorials.tutorial3.step6')
      ]
    },
    {
      id: 4,
      title: t('tutorials.tutorial4.title'),
      category: "guidelines",
      duration: "6:20",
      description: t('tutorials.tutorial4.description'),
      thumbnail: "📚",
      steps: [
        t('tutorials.tutorial4.step1'),
        t('tutorials.tutorial4.step2'),
        t('tutorials.tutorial4.step3'),
        t('tutorials.tutorial4.step4'),
        t('tutorials.tutorial4.step5'),
        t('tutorials.tutorial4.step6')
      ]
    },
    {
      id: 5,
      title: t('tutorials.tutorial5.title'),
      category: "verification",
      duration: "5:00",
      description: t('tutorials.tutorial5.description'),
      thumbnail: "✅",
      steps: [
        t('tutorials.tutorial5.step1'),
        t('tutorials.tutorial5.step2'),
        t('tutorials.tutorial5.step3'),
        t('tutorials.tutorial5.step4'),
        t('tutorials.tutorial5.step5'),
        t('tutorials.tutorial5.step6')
      ]
    },
    {
      id: 6,
      title: t('tutorials.tutorial6.title'),
      category: "recommendations",
      duration: "3:45",
      description: t('tutorials.tutorial6.description'),
      thumbnail: "🚨",
      steps: [
        t('tutorials.tutorial6.step1'),
        t('tutorials.tutorial6.step2'),
        t('tutorials.tutorial6.step3'),
        t('tutorials.tutorial6.step4'),
        t('tutorials.tutorial6.step5')
      ]
    },
    {
      id: 7,
      title: t('tutorials.tutorial7.title'),
      category: "recommendations",
      duration: "5:15",
      description: t('tutorials.tutorial7.description'),
      thumbnail: "📋",
      steps: [
        t('tutorials.tutorial7.step1'),
        t('tutorials.tutorial7.step2'),
        t('tutorials.tutorial7.step3'),
        t('tutorials.tutorial7.step4'),
        t('tutorials.tutorial7.step5'),
        t('tutorials.tutorial7.step6')
      ]
    },
    {
      id: 8,
      title: t('tutorials.tutorial8.title'),
      category: "support",
      duration: "4:00",
      description: t('tutorials.tutorial8.description'),
      thumbnail: "💬",
      steps: [
        t('tutorials.tutorial8.step1'),
        t('tutorials.tutorial8.step2'),
        t('tutorials.tutorial8.step3'),
        t('tutorials.tutorial8.step4'),
        t('tutorials.tutorial8.step5'),
        t('tutorials.tutorial8.step6')
      ]
    }
  ];

  const categories = [
    { id: 'all', name: t('tutorials.allTutorials'), icon: 'Grid' },
    { id: 'basics', name: t('tutorials.gettingStarted'), icon: 'Play' },
    { id: 'recommendations', name: t('tutorials.recommendations'), icon: 'FileText' },
    { id: 'guidelines', name: t('tutorials.guidelinesCategory'), icon: 'BookOpen' },
    { id: 'billing', name: t('tutorials.billingCredits'), icon: 'CreditCard' },
    { id: 'verification', name: t('tutorials.verification'), icon: 'CheckCircle2' },
    { id: 'support', name: t('tutorials.support'), icon: 'HelpCircle' }
  ];

  const filteredVideos = selectedCategory === 'all' 
    ? videoTutorials 
    : videoTutorials?.filter(v => v?.category === selectedCategory);

  const handleVideoClick = (video) => {
    setActiveVideo(activeVideo?.id === video?.id ? null : video);
  };

  return (
    <div className="min-h-screen bg-background">
      <PrimaryNavigation 
        userRole={profile?.role || 'clinician'} 
        creditBalance={profile?.credits_remaining || 0}
        subscriptionTier={profile?.subscription_tier || 'Free'} 
      />

      <div className="pt-header">
        <div className="max-w-7xl mx-auto container-padding py-6 sm:py-8 md:py-12">
          {/* Header */}
          <div className="mb-8">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h1 className="text-3xl font-bold text-foreground mb-2">{t('tutorials.title')}</h1>
                <p className="text-muted-foreground">
                  {t('tutorials.subtitle')}
                </p>
              </div>
              <Button 
                variant="outline" 
                iconName="ArrowLeft"
                onClick={() => navigate('/dashboard')}
              >
                {t('common.back')}
              </Button>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
            {/* Categories Sidebar */}
            <div className="lg:col-span-1">
              <div className="clinical-card p-4 sticky top-24">
                <h3 className="font-semibold text-foreground mb-4">{t('tutorials.categories')}</h3>
                <div className="space-y-2">
                  {categories?.map(category => (
                    <button
                      key={category?.id}
                      onClick={() => setSelectedCategory(category?.id)}
                      className={`
                        w-full flex items-center gap-3 p-3 rounded-lg transition-all
                        ${selectedCategory === category?.id 
                          ? 'bg-primary text-white' :'hover:bg-muted text-foreground'}
                      `}
                    >
                      <Icon 
                        name={category?.icon} 
                        size={18} 
                        color={selectedCategory === category?.id ? 'white' : 'currentColor'}
                      />
                      <span className="text-sm font-medium">{category?.name}</span>
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Video Tutorials List */}
            <div className="lg:col-span-3 space-y-4">
              {filteredVideos?.map(video => (
                <div key={video?.id} className="clinical-card">
                  <button
                    onClick={() => handleVideoClick(video)}
                    className="w-full p-6 text-left hover:bg-muted/50 transition-all"
                  >
                    <div className="flex items-start gap-4">
                      <div className="flex-shrink-0 w-16 h-16 bg-primary/10 rounded-lg flex items-center justify-center text-3xl">
                        {video?.thumbnail}
                      </div>
                      <div className="flex-1">
                        <div className="flex items-start justify-between mb-2">
                          <h3 className="text-lg font-semibold text-foreground">
                            {video?.title}
                          </h3>
                          <div className="flex items-center gap-2">
                            <span className="text-sm text-muted-foreground flex items-center gap-1">
                              <Icon name="Clock" size={14} />
                              {video?.duration}
                            </span>
                            <Icon 
                              name={activeVideo?.id === video?.id ? "ChevronUp" : "ChevronDown"} 
                              size={20} 
                              color="var(--color-muted-foreground)"
                            />
                          </div>
                        </div>
                        <p className="text-sm text-muted-foreground">
                          {video?.description}
                        </p>
                      </div>
                    </div>
                  </button>

                  {/* Expanded Video Steps */}
                  {activeVideo?.id === video?.id && (
                    <div className="px-6 pb-6 border-t border-border mt-4 pt-4">
                      <h4 className="font-semibold text-foreground mb-3 flex items-center gap-2">
                        <Icon name="List" size={18} />
                        {t('tutorials.tutorialOutline')}
                      </h4>
                      <div className="space-y-2">
                        {video?.steps?.map((step, index) => (
                          <div key={index} className="flex items-start gap-3 p-3 bg-muted/30 rounded-lg">
                            <div className="flex-shrink-0 w-6 h-6 bg-primary text-white rounded-full flex items-center justify-center text-xs font-bold">
                              {index + 1}
                            </div>
                            <p className="text-sm text-foreground flex-1">{step}</p>
                          </div>
                        ))}
                      </div>
                      <div className="mt-4 p-4 bg-primary/10 rounded-lg border border-primary/20">
                        <div className="flex items-start gap-3">
                          <Icon name="Video" size={20} color="var(--color-primary)" className="mt-0.5" />
                          <div className="flex-1">
                            <p className="text-sm font-medium text-foreground mb-1">
                              {t('tutorials.videoTutorialSimulation')}
                            </p>
                            <p className="text-xs text-muted-foreground mb-3">
                              {t('tutorials.videoDescription')} <a href="mailto:care@mydocrefer.org" className="text-primary hover:underline">care@mydocrefer.org</a>
                            </p>
                            <Button
                              variant="default"
                              size="sm"
                              iconName="Play"
                              onClick={() => {
                                if (video?.category === 'recommendations' && video?.id === 2) {
                                  navigate('/clinical-recommendation');
                                } else if (video?.category === 'guidelines') {
                                  navigate('/guidelines-reference');
                                } else if (video?.category === 'support') {
                                  navigate('/contact-support');
                                } else if (video?.category === 'billing') {
                                  navigate('/account-settings');
                                }
                              }}
                            >
                              {t('tutorials.tryThisFeature')}
                            </Button>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Help Section */}
          <div className="clinical-card p-6 mt-8 bg-primary/10 border border-primary/20">
            <div className="flex items-start gap-3">
              <Icon name="MessageCircle" size={20} color="var(--color-primary)" className="mt-0.5" />
              <div className="flex-1">
                <h4 className="font-semibold text-foreground mb-2">{t('tutorials.needMoreHelp')}</h4>
                <p className="text-sm text-muted-foreground mb-3">
                  {t('tutorials.helpDescription')}
                </p>
                <div className="flex items-center gap-3">
                  <Button
                    variant="outline"
                    size="sm"
                    iconName="Mail"
                    onClick={() => navigate('/contact-support')}
                  >
                    {t('tutorials.contactSupport')}
                  </Button>
                  <a 
                    href="mailto:care@mydocrefer.org" 
                    className="text-sm text-primary hover:underline flex items-center gap-1"
                  >
                    care@mydocrefer.org
                    <Icon name="ExternalLink" size={14} />
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserGuide;